import React, { useState, useEffect, useRef } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import './AdminComponents.css';

interface StrategyLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  strategyName: string;
}

const StrategyLogsModal: React.FC<StrategyLogsModalProps> = ({ isOpen, onClose, strategyName }) => {
  const adminApiService = useAdminApiService();
  const [logs, setLogs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lineCount, setLineCount] = useState(500);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchLogs = async () => {
    if (!strategyName) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminApiService.getStrategyLogs(strategyName, lineCount);
      if (response.success && response.data) {
        setLogs(response.data.logs || []);
        // Only auto-scroll to bottom on desktop, not on mobile (mobile should show top/header)
        if (window.innerWidth > 768) {
          setTimeout(scrollToBottom, 100);
        }
      } else {
        setError(response.error || 'Failed to fetch logs');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch logs');
      console.error('Error fetching strategy logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && strategyName) {
      fetchLogs();
    }
  }, [isOpen, strategyName, lineCount]);

  // Scroll to top when modal opens on mobile
  useEffect(() => {
    if (isOpen) {
      // On mobile, scroll to top when modal opens
      if (window.innerWidth <= 768) {
        // Function to perform all scroll operations
        const performScroll = () => {
          // Scroll window and document
          window.scrollTo(0, 0);
          if (document.documentElement) {
            document.documentElement.scrollTop = 0;
          }
          if (document.body) {
            document.body.scrollTop = 0;
          }
          
          // Scroll the admin-content container (this is the scrollable container on mobile)
          const adminContent = document.querySelector('.admin-content') as HTMLElement;
          if (adminContent) {
            adminContent.scrollTop = 0;
            adminContent.scrollTo(0, 0);
          }
          // Also try scrolling the admin-main if it exists
          const adminMain = document.querySelector('.admin-main') as HTMLElement;
          if (adminMain) {
            adminMain.scrollTop = 0;
          }
          // Try scrolling admin-content-body as well
          const adminContentBody = document.querySelector('.admin-content-body') as HTMLElement;
          if (adminContentBody) {
            adminContentBody.scrollTop = 0;
          }
          
          // CRITICAL: Scroll the modal content itself to top
          if (modalContentRef.current) {
            modalContentRef.current.scrollTop = 0;
            modalContentRef.current.scrollTo(0, 0);
          }
          
          // CRITICAL: Scroll the modal overlay to top (this is the scrollable container)
          if (overlayRef.current) {
            overlayRef.current.scrollTop = 0;
            overlayRef.current.scrollTo(0, 0);
          }
          
          // Also try scrolling any modal overlay by class
          const modalOverlay = document.querySelector('.modal-overlay') as HTMLElement;
          if (modalOverlay) {
            modalOverlay.scrollTop = 0;
            modalOverlay.scrollTo(0, 0);
          }
        };
        
        // Perform scroll immediately
        performScroll();
        
        // Also perform scroll after render using double RAF to ensure DOM is ready
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            performScroll();
            // Additional scroll after a small delay to ensure it works
            setTimeout(() => {
              performScroll();
            }, 100);
          });
        });
        
        // Prevent body scroll while modal is open
        document.body.classList.add('modal-open');
      }
    } else {
      // Restore body scroll when modal closes
      if (window.innerWidth <= 768) {
        document.body.classList.remove('modal-open');
      }
    }
    
    // Cleanup: restore body scroll on unmount
    return () => {
      if (window.innerWidth <= 768) {
        document.body.classList.remove('modal-open');
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && autoRefresh) {
      refreshIntervalRef.current = setInterval(() => {
        fetchLogs();
      }, 5000); // Refresh every 5 seconds
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [isOpen, autoRefresh, strategyName, lineCount]);

  const handleCopyLogs = () => {
    const logsText = logs.join('\n');
    navigator.clipboard.writeText(logsText);
    alert('Logs copied to clipboard!');
  };

  const handleDownloadLogs = () => {
    const logsText = logs.join('\n');
    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${strategyName}-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (!isOpen) return null;

  const isMobile = window.innerWidth <= 768;

  return (
    <div 
      ref={overlayRef}
      className="modal-overlay" 
      onClick={onClose} 
      style={{ 
        zIndex: 10000,
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: isMobile ? 'flex-start' : 'center',
        padding: isMobile ? '0' : '20px',
        overflow: isMobile ? 'auto' : 'auto',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <div
        ref={modalContentRef}
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? '100vw' : '90vw',
          maxWidth: isMobile ? '100vw' : '1200px',
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '85vh',
          margin: isMobile ? '0' : 'auto',
          borderRadius: isMobile ? '0' : '12px',
          display: 'flex',
          flexDirection: 'column',
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? '0' : 'auto',
          left: isMobile ? '0' : 'auto',
          right: isMobile ? '0' : 'auto',
          bottom: isMobile ? '0' : 'auto',
          zIndex: isMobile ? 10001 : 'auto',
          overflow: isMobile ? 'auto' : 'hidden',
          WebkitOverflowScrolling: isMobile ? 'touch' : 'auto'
        }}
      >
        {/* Header */}
        <div
          className="modal-header"
          style={{
            padding: isMobile ? '16px 12px' : '20px',
            borderBottom: '2px solid #e5e7eb',
            position: 'relative',
            top: 'auto',
            backgroundColor: 'white',
            zIndex: 100,
            flexShrink: 0,
            minHeight: isMobile ? '70px' : 'auto',
            boxShadow: isMobile ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h2 className="modal-title" style={{ fontSize: isMobile ? '18px' : '22px', margin: 0, marginBottom: '4px' }}>
                📋 Strategy Logs
              </h2>
              <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#6b7280', margin: 0 }}>
                Viewing logs for: <strong style={{ color: '#3b82f6' }}>{strategyName}</strong>
              </p>
            </div>
            <button
              className="modal-close"
              onClick={onClose}
              style={{
                fontSize: isMobile ? '32px' : '28px',
                padding: isMobile ? '8px 12px' : '8px 12px',
                minWidth: isMobile ? '48px' : 'auto',
                minHeight: isMobile ? '48px' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#333',
                fontWeight: 'bold',
                lineHeight: '1',
                flexShrink: 0
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Controls */}
        <div
          style={{
            padding: isMobile ? '12px' : '16px',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 500 }}>Lines:</label>
            <select
              value={lineCount}
              onChange={(e) => setLineCount(Number(e.target.value))}
              style={{
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: isMobile ? '12px' : '14px',
                cursor: 'pointer'
              }}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: isMobile ? '12px' : '14px' }}>Auto-refresh (5s)</span>
          </label>

          <button
            onClick={fetchLogs}
            disabled={loading}
            className="btn btn-secondary"
            style={{ fontSize: isMobile ? '12px' : '14px', padding: '6px 12px' }}
          >
            {loading ? '🔄 Refreshing...' : '🔄 Refresh'}
          </button>

          <button
            onClick={handleCopyLogs}
            className="btn btn-secondary"
            style={{ fontSize: isMobile ? '12px' : '14px', padding: '6px 12px' }}
          >
            📋 Copy
          </button>

          <button
            onClick={handleDownloadLogs}
            className="btn btn-secondary"
            style={{ fontSize: isMobile ? '12px' : '14px', padding: '6px 12px' }}
          >
            💾 Download
          </button>
        </div>

        {/* Logs Display */}
        <div
          className="modal-body"
          style={{
            padding: 0,
            flex: 1,
            overflow: 'hidden',
            backgroundColor: '#1e293b',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          {error && (
            <div className="alert alert-error" style={{ margin: '16px' }}>
              {error}
            </div>
          )}

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: isMobile ? '12px' : '16px',
              fontFamily: 'Monaco, Menlo, "Courier New", monospace',
              fontSize: isMobile ? '10px' : '12px',
              lineHeight: '1.6',
              color: '#e2e8f0',
              backgroundColor: '#1e293b',
              minHeight: 0,
              // Custom scrollbar styling for better visibility
              scrollbarWidth: 'thin',
              scrollbarColor: '#64748b #1e293b'
            }}
            // Add CSS for webkit browsers (Chrome, Safari, Edge)
            className="strategy-logs-scrollable"
          >
            {loading && logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                Loading logs...
              </div>
            ) : logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                No logs found for strategy "{strategyName}"
              </div>
            ) : (
              <>
                {logs.map((log, index) => {
                  // Color-code log levels
                  let color = '#e2e8f0'; // default
                  if (log.includes('ERROR') || log.includes('CRITICAL')) {
                    color = '#fca5a5'; // red
                  } else if (log.includes('WARN')) {
                    color = '#fcd34d'; // yellow
                  } else if (log.includes('INFO')) {
                    color = '#93c5fd'; // blue
                  } else if (log.includes('DEBUG')) {
                    color = '#a78bfa'; // purple
                  }

                  return (
                    <div key={index} style={{ marginBottom: '2px', color, wordBreak: 'break-word' }}>
                      {log}
                    </div>
                  );
                })}
                <div ref={logsEndRef} />
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: isMobile ? '12px' : '16px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            fontSize: isMobile ? '11px' : '12px',
            color: '#6b7280',
            textAlign: 'center'
          }}
        >
          Showing last {lineCount} lines {autoRefresh && '• Auto-refreshing every 5 seconds'} • 
          {logs.length} log{logs.length !== 1 ? 's' : ''} displayed
        </div>
      </div>
    </div>
  );
};

export default StrategyLogsModal;

